/**
 * Meta Pixel, loaded only after marketing consent.
 *
 * It used to sit inline in index.html and fire `init` + `PageView` on every load, before
 * the cookie banner was answered, while the cookie policy promised marketing cookies only
 * after explicit consent. MetaPixelManager now calls these helpers from the consent state.
 */

export const META_PIXEL_SCRIPT_ID = "thinkeat-meta-pixel";
const META_PIXEL_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const DEFAULT_META_PIXEL_ID = "1517723679772098";
const META_COOKIES = ["_fbp", "_fbc"];

type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[][];
  push: FbqFunction;
  loaded: boolean;
  version: string;
};

type MetaPixelWindow = Window & { fbq?: FbqFunction; _fbq?: FbqFunction };

/** `VITE_META_PIXEL_ID` overrides the production ID; an empty value disables the pixel. */
export function getMetaPixelId(): string {
  const configured = import.meta.env.VITE_META_PIXEL_ID;
  return (configured === undefined ? DEFAULT_META_PIXEL_ID : configured).trim();
}

/** Same queue stub as Meta's official snippet: calls made before the script arrives are replayed. */
function installFbqStub(target: MetaPixelWindow): FbqFunction {
  if (target.fbq) return target.fbq;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as FbqFunction;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";

  target.fbq = fbq;
  if (!target._fbq) target._fbq = fbq;
  return fbq;
}

/** Loads the pixel and sends a PageView. Returns false when there is nothing to load. */
export function loadMetaPixel(pixelId: string = getMetaPixelId()): boolean {
  if (typeof window === "undefined" || typeof document === "undefined" || !pixelId) {
    return false;
  }

  const fbq = installFbqStub(window as MetaPixelWindow);
  fbq("consent", "grant");

  if (document.getElementById(META_PIXEL_SCRIPT_ID)) {
    return false;
  }

  fbq("init", pixelId);
  fbq("track", "PageView");

  const script = document.createElement("script");
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = META_PIXEL_SRC;
  document.head.appendChild(script);

  return true;
}

function expireCookie(name: string): void {
  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
  document.cookie = `${name}=; ${expired}`;

  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  for (let i = 0; i < parts.length - 1; i++) {
    document.cookie = `${name}=; ${expired}; domain=.${parts.slice(i).join(".")}`;
  }
}

/** Consent revoked: stop the pixel, drop its script tag and its first-party cookies. */
export function revokeMetaPixel(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  (window as MetaPixelWindow).fbq?.("consent", "revoke");
  document.getElementById(META_PIXEL_SCRIPT_ID)?.remove();
  META_COOKIES.forEach(expireCookie);
}
